<template>
  <v-dialog persistent scrollable width="800" v-model="dialog" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>

      <!-- HEADER: nome do paciente -->
      <v-card-title class="d-flex align-center pb-0">
        <span class="subtitle-1 font-weight-medium mr-auto text-truncate">Registro da sessão</span>
      </v-card-title>

      <!-- STATUS DA SESSÃO -->
      <div class="px-4 pb-3">
        <v-chip-group mandatory v-model="session.status">
          <v-chip small filter :value="'pending'">Pendente</v-chip>
          <v-chip small filter :value="'missed'" active-class="secondary">
            Faltou
          </v-chip>
          <v-chip small filter :value="'shown'" active-class="primary">
            Compareceu
          </v-chip>
        </v-chip-group>
      </div>

      <!-- NAVEGAÇÃO POR ABAS -->
      <v-tabs v-model="activeTab" color="primary" class="flex-grow-0" :grow="$vuetify.breakpoint.xsOnly">
        <v-tab>
          <v-icon small left>mdi-note-text-outline</v-icon>
          Sessão
        </v-tab>

        <v-tab>
          <v-icon small left>mdi-paperclip</v-icon>
          Arquivos
          <v-chip v-if="hasFiles" x-small color="primary" class="ml-2">{{ files.data.length }}</v-chip>
        </v-tab>
      </v-tabs>

      <v-divider />

      <!-- CONTEÚDO DAS ABAS (área scrollável) -->
      <v-card-text class="pa-0">
        <v-tabs-items v-model="activeTab">

          <!-- ══════════════════════════════
               ABA 1 — ANOTAÇÕES
               ══════════════════════════════ -->
          <v-tab-item>
            <div class="pa-4 pa-md-6">

              <template v-if="canEditSessionAnnotations">
                <!-- Textarea principal -->
                <v-textarea v-model="session.annotations" solo flat placeholder="Faça aqui suas anotações..."
                  background-color="grey lighten-4" no-resize hide-details rows="12" />

                <!-- Indicador de autosave -->
                <transition name="autosave-fade">
                  <span v-if="autoSaveStatus" class="caption grey--text d-flex align-center justify-end mt-1">
                    <v-progress-circular v-if="autoSaveStatus === 'saving'" indeterminate size="10" width="1"
                      color="grey" class="mr-1" />
                    <v-icon v-else x-small color="grey darken-1" class="mr-1">mdi-check-circle</v-icon>
                    {{ autoSaveStatus === 'saving' ? 'Salvando...' : 'Salvo automaticamente' }}
                  </span>
                </transition>

                <!-- Barra de ações -->
                <div class="d-flex flex-wrap align-center mt-3" style="gap: 8px;">
                  <app-btn v-if="canUserIa" small color="primary" @click="handleTextGenerationForm">
                    <app-icon left class="mr-1">wand_stars</app-icon>
                    Melhorar com IA
                  </app-btn>
                </div>
                <!-- Seção de transcrição inline -->
                <template v-if="hasCustomer">
                  <v-divider class="my-4" />
                  <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
                    <div class="caption secondary--text font-weight-medium text-uppercase">Transcrição por IA</div>
                    <v-chip v-if="transcriptionLimitReached" x-small color="error" outlined>
                      <v-icon left x-small>mdi-alert-circle-outline</v-icon>
                      Limite de transcrições do mês atingido
                    </v-chip>
                  </div>

                  <!-- Vazio / Gravador inline -->
                  <div v-if="!transcription.status || (recorderState && recorderState !== 'stopped')">
                  <transition name="recorder-fade" mode="out-in">

                    <!-- Botões iniciais (Gravar/Enviar arquivo ficam sempre visíveis; Presencial/Online
                         expandem logo abaixo ao clicar em Gravar, sem esconder os botões iniciais) -->
                    <div v-if="!recorderState || recorderState === 'mode'" key="initial">
                      <div class="d-flex flex-wrap" style="gap: 8px;">
                        <v-tooltip bottom :disabled="!isRecordingOtherSession">
                          <template v-slot:activator="{ on, attrs }">
                            <div v-bind="attrs" v-on="on">
                              <app-btn small outlined :color="recorderState === 'mode' ? 'primary' : 'grey darken-2'"
                                :disabled="isRecordingOtherSession || transcriptionLimitReached" @click="toggleModeChoice">
                                <v-icon left small>mdi-microphone</v-icon>
                                Gravar
                                <v-icon right small>{{ recorderState === 'mode' ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                              </app-btn>
                            </div>
                          </template>
                          <span>Já existe uma gravação em andamento em outra sessão</span>
                        </v-tooltip>
                        <app-btn small outlined color="grey darken-2" :disabled="transcriptionLimitReached"
                          @click="openTranscriptionPanel">
                          <v-icon left small>mdi-upload</v-icon>
                          Enviar arquivo
                        </app-btn>
                      </div>

                      <v-expand-transition>
                        <div v-if="recorderState === 'mode'" class="mt-3 pt-3"
                          style="border-top: 1px solid rgba(0,0,0,.08);">
                          <div class="caption grey--text text-uppercase font-weight-medium mb-2">Como será a sessão?</div>
                          <div class="d-flex flex-wrap" style="gap: 8px;">
                            <app-btn small outlined color="grey darken-2" @click="chooseMode('mic')">
                              <v-icon left small>mdi-microphone</v-icon>
                              Presencial
                            </app-btn>
                            <v-tooltip bottom :disabled="supportsMeetingMode">
                              <template v-slot:activator="{ on, attrs }">
                                <div v-bind="attrs" v-on="on">
                                  <app-btn small outlined color="grey darken-2" :disabled="!supportsMeetingMode"
                                    @click="chooseMode('meeting')">
                                    <v-icon left small>mdi-monitor</v-icon>
                                    Online
                                  </app-btn>
                                </div>
                              </template>
                              <span>Não suportado neste navegador</span>
                            </v-tooltip>
                          </div>
                        </div>
                      </v-expand-transition>
                    </div>

                    <!-- Instrução do modo online -->
                    <div v-else-if="recorderState === 'online-guide'" key="online-guide" class="recorder-box text-left">
                      <div class="d-flex align-center mb-3">
                        <v-icon color="primary" small class="mr-2">mdi-information-outline</v-icon>
                        <span class="body-2 font-weight-medium">Ao compartilhar, marque a opção de áudio</span>
                      </div>

                      <div class="online-guide-row d-flex align-center">
                        <span class="caption grey--text online-guide-label">Aba do navegador</span>
                        <v-icon x-small color="grey" class="mx-1">mdi-arrow-right</v-icon>
                        <span class="ui-label">Compartilhar áudio da guia</span>
                      </div>
                      <div class="online-guide-row d-flex align-center mt-2">
                        <span class="caption grey--text online-guide-label">Tela inteira</span>
                        <v-icon x-small color="grey" class="mx-1">mdi-arrow-right</v-icon>
                        <span class="ui-label">Áudio do sistema</span>
                      </div>

                      <div class="caption grey--text mt-3">
                        Meet/Zoom Web → aba · Zoom/Teams desktop → tela inteira (Windows)
                      </div>

                      <div class="d-flex justify-center mt-4" style="gap: 8px;">
                        <app-btn small outlined color="grey darken-2" @click="openModeChoice">Voltar</app-btn>
                        <app-btn small color="primary" depressed @click="startOnlineCapture">
                          <v-icon left small>mdi-monitor-share</v-icon>
                          Iniciar captura
                        </app-btn>
                      </div>
                    </div>

                    <!-- Solicitando permissão -->
                    <div v-else-if="recorderState === 'requesting'" key="requesting" class="recorder-box">
                      <v-progress-circular indeterminate color="primary" size="20" width="2" class="mb-2" />
                      <div class="body-2 grey--text mb-3">
                        {{ recordingModeText }}
                      </div>
                      <app-btn small outlined color="grey darken-2" @click="cancelRecording">Cancelar</app-btn>
                    </div>

                    <!-- Gravando -->
                    <div v-else-if="recorderState === 'recording'" key="recording" class="recorder-box">
                      <div class="recorder-pulse mb-2" :class="{ 'recorder-pulse--paused': isPaused }">
                        <v-icon :color="isPaused ? 'grey darken-1' : 'red'">mdi-microphone</v-icon>
                      </div>
                      <div class="text-h6 font-weight-bold mb-1">{{ formattedDuration }}</div>
                      <div class="caption grey--text mb-4">
                        {{ recordingModeText }}
                      </div>
                      <div class="d-flex justify-center" style="gap: 8px;">
                        <app-btn small outlined color="grey darken-2" @click="togglePause">
                          <v-icon left small>{{ isPaused ? 'mdi-play' : 'mdi-pause' }}</v-icon>
                          {{ isPaused ? 'Retomar' : 'Pausar' }}
                        </app-btn>
                        <app-btn small color="red" dark depressed @click="stopRecording">
                          <v-icon left small>mdi-stop</v-icon>
                          Parar gravação
                        </app-btn>
                      </div>
                    </div>

                    <!-- Gravação concluída -->
                    <div v-else-if="recorderState === 'stopped'" key="stopped" class="recorder-box">
                      <v-icon color="success" size="32" class="mb-2">mdi-check-circle-outline</v-icon>
                      <div class="body-2 font-weight-medium mb-1">Gravação concluída</div>
                      <div class="caption grey--text mb-4">{{ formattedDuration }}</div>
                      <div class="d-flex justify-center flex-wrap" style="gap: 8px;">
                        <app-btn small color="primary" depressed :disabled="transcriptionLimitReached"
                          @click="sendRecording">
                          <v-icon left small>mdi-send</v-icon>
                          Transcrever
                        </app-btn>
                        <app-btn small outlined color="grey darken-2" @click="downloadRecording">
                          <v-icon left small>mdi-download</v-icon>
                          Baixar áudio
                        </app-btn>
                        <app-btn small outlined :color="restartConfirming ? 'error' : undefined"
                          @click="handleRestartRecording">
                          <v-icon left small>{{ restartConfirming ? 'mdi-alert' : 'mdi-refresh' }}</v-icon>
                          {{ restartConfirming ? 'Isso descarta a gravação atual, Confirmar?' : 'Regravar' }}
                        </app-btn>
                      </div>
                    </div>

                    <!-- Erro -->
                    <div v-else-if="recorderState === 'error'" key="error" class="recorder-box">
                      <v-icon color="error" size="32" class="mb-2">mdi-microphone-off</v-icon>
                      <div class="body-2 error--text mb-4">{{ recorderErrorMessage }}</div>
                      <div class="d-flex justify-center" style="gap: 8px;">
                        <app-btn small outlined color="grey darken-2" @click="cancelRecording">Fechar</app-btn>
                        <app-btn small color="primary" depressed @click="retryAfterError">
                          <v-icon left small>mdi-reload</v-icon>
                          Tentar novamente
                        </app-btn>
                      </div>
                    </div>

                  </transition>
                  </div>

                  <!-- Processando -->
                  <div v-else-if="transcription.status !== 'done' && transcription.status !== 'error'" class="py-2">
                    <div class="body-2 mb-2">{{ transcriptionStatus.label }}</div>
                    <div v-if="recoveredPendingSend" class="caption grey--text mb-2">
                      Gravação recuperada automaticamente após reabrir a sessão.
                    </div>
                    <v-progress-linear indeterminate color="primary" rounded height="4" style="max-width: 240px;" />
                  </div>

                  <!-- Erro: a gravação nunca é descartada aqui — sempre dá pra tentar de novo ou baixar -->
                  <div v-else-if="transcription.status === 'error'" class="py-2">
                    <v-alert outlined type="error" dense class="mb-3">
                      {{ transcription.error || 'Erro no processamento da transcrição.' }}
                    </v-alert>
                    <div class="d-flex flex-wrap" style="gap: 8px;">
                      <app-btn small color="primary" depressed :disabled="transcriptionLimitReached"
                        @click="retryTranscriptionAttempt">
                        <v-icon left small>mdi-reload</v-icon>
                        Tentar novamente
                      </app-btn>
                      <app-btn v-if="hasRecordedAudio" small outlined color="grey darken-2" @click="downloadRecording">
                        <v-icon left small>mdi-download</v-icon>
                        Baixar áudio
                      </app-btn>
                      <app-btn small outlined :color="restartConfirming ? 'error' : undefined"
                        @click="handleRestartRecording">
                        <v-icon left small>{{ restartConfirming ? 'mdi-alert' : 'mdi-refresh' }}</v-icon>
                        {{ restartConfirming ? 'Isso descarta a gravação atual, Confirmar?' : 'Regravar' }}
                      </app-btn>
                    </div>
                  </div>

                  <!-- Concluído -->
                  <div v-else-if="transcription.status === 'done'">
                    <v-tabs v-model="transcriptionTab" color="primary" show-arrows>
                      <v-tab v-if="transcription.transcript">Transcrição</v-tab>
                      <v-tab v-if="transcription.summary">Resumo</v-tab>
                      <v-tab v-if="transcription.sentiment_analysis">Análise Emocional</v-tab>
                      <v-tab v-if="transcription.medical_record">Prontuário</v-tab>
                    </v-tabs>
                    <v-divider />
                    <v-tabs-items v-model="transcriptionTab">

                      <v-tab-item v-if="transcription.transcript">
                        <div class="pa-4">
                          <p class="body-2 mb-4">{{ transcription.transcript }}</p>
                          <app-btn small depressed color="primary" @click="appendTranscriptToAnnotations">
                            <v-icon left small>mdi-plus</v-icon>
                            Inserir nas anotações
                          </app-btn>
                        </div>
                      </v-tab-item>

                      <v-tab-item v-if="transcription.summary">
                        <div class="pa-4">
                          <div
                            v-if="transcription.summary.principais_temas && transcription.summary.principais_temas.length"
                            class="mb-3">
                            <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Principais temas
                            </div>
                            <v-chip-group column>
                              <v-chip small v-for="(tema, i) in transcription.summary.principais_temas" :key="i">{{ tema
                                }}</v-chip>
                            </v-chip-group>
                          </div>
                          <div v-if="transcription.summary.progresso_observado" class="mb-3">
                            <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Progresso
                              observado</div>
                            <p class="body-2 mb-0">{{ transcription.summary.progresso_observado }}</p>
                          </div>
                          <div
                            v-if="transcription.summary.pontos_de_atencao && transcription.summary.pontos_de_atencao.length"
                            class="mb-3">
                            <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Pontos de
                              atenção</div>
                            <ul class="pl-4 mb-0">
                              <li v-for="(ponto, i) in transcription.summary.pontos_de_atencao" :key="i" class="body-2">
                                {{ ponto }}</li>
                            </ul>
                          </div>
                          <app-btn small depressed color="primary" @click="appendSummaryToAnnotations">
                            <v-icon left small>mdi-plus</v-icon>
                            Inserir nas anotações
                          </app-btn>
                        </div>
                      </v-tab-item>

                      <v-tab-item v-if="transcription.sentiment_analysis">
                        <div class="pa-4">
                          <v-row dense>
                            <v-col cols="12" v-if="transcription.sentiment_analysis.estado_emocional_geral"
                              class="mb-2">
                              <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Estado
                                emocional geral</div>
                              <p class="body-2 mb-0">{{ transcription.sentiment_analysis.estado_emocional_geral }}</p>
                            </v-col>
                            <v-col cols="12" md="6" v-if="transcription.sentiment_analysis.nivel_engajamento"
                              class="mb-2">
                              <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Nível de
                                engajamento</div>
                              <p class="body-2 mb-0 text-capitalize">{{
                                transcription.sentiment_analysis.nivel_engajamento }}</p>
                            </v-col>
                            <v-col cols="12"
                              v-if="transcription.sentiment_analysis.emocoes_identificadas && transcription.sentiment_analysis.emocoes_identificadas.length"
                              class="mb-2">
                              <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Emoções
                                identificadas</div>
                              <v-chip-group column>
                                <v-chip small v-for="(e, i) in transcription.sentiment_analysis.emocoes_identificadas"
                                  :key="i" class="text-capitalize">{{ e }}</v-chip>
                              </v-chip-group>
                            </v-col>
                            <v-col cols="12"
                              v-if="transcription.sentiment_analysis.momentos_criticos && transcription.sentiment_analysis.momentos_criticos.length"
                              class="mb-2">
                              <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Momentos
                                críticos</div>
                              <ul class="pl-4 mb-0">
                                <li v-for="(m, i) in transcription.sentiment_analysis.momentos_criticos" :key="i"
                                  class="body-2">{{ m }}
                                </li>
                              </ul>
                            </v-col>
                            <v-col cols="12" v-if="transcription.sentiment_analysis.observacoes_clinicas" class="mb-2">
                              <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Observações
                                clínicas</div>
                              <p class="body-2 mb-0">{{ transcription.sentiment_analysis.observacoes_clinicas }}</p>
                            </v-col>
                          </v-row>
                          <app-btn small depressed color="primary" class="mt-2" @click="appendSentimentToAnnotations">
                            <v-icon left small>mdi-plus</v-icon>
                            Inserir nas anotações
                          </app-btn>
                        </div>
                      </v-tab-item>

                      <v-tab-item v-if="transcription.medical_record">
                        <div class="pa-4">
                          <v-row dense>
                            <v-col cols="12" v-if="transcription.medical_record.queixa_principal" class="mb-2">
                              <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Queixa
                                principal</div>
                              <p class="body-2 mb-0">{{ transcription.medical_record.queixa_principal }}</p>
                            </v-col>
                            <v-col cols="12" v-if="transcription.medical_record.conteudo_sessao" class="mb-2">
                              <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Conteúdo da
                                sessão</div>
                              <p class="body-2 mb-0">{{ transcription.medical_record.conteudo_sessao }}</p>
                            </v-col>
                            <v-col cols="12"
                              v-if="transcription.medical_record.intervencoes_realizadas && transcription.medical_record.intervencoes_realizadas.length"
                              class="mb-2">
                              <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Intervenções
                                realizadas</div>
                              <v-chip-group column>
                                <v-chip small v-for="(int, i) in transcription.medical_record.intervencoes_realizadas"
                                  :key="i">{{ int
                                  }}</v-chip>
                              </v-chip-group>
                            </v-col>
                            <v-col cols="12" v-if="transcription.medical_record.reacoes_do_paciente" class="mb-2">
                              <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Reações do
                                paciente</div>
                              <p class="body-2 mb-0">{{ transcription.medical_record.reacoes_do_paciente }}</p>
                            </v-col>
                            <v-col cols="12" v-if="transcription.medical_record.plano_proxima_sessao" class="mb-2">
                              <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Plano para
                                próxima sessão</div>
                              <p class="body-2 mb-0">{{ transcription.medical_record.plano_proxima_sessao }}</p>
                            </v-col>
                            <v-col cols="12" v-if="transcription.medical_record.observacoes_adicionais" class="mb-2">
                              <div class="caption secondary--text font-weight-medium text-uppercase mb-1">Observações
                                adicionais</div>
                              <p class="body-2 mb-0">{{ transcription.medical_record.observacoes_adicionais }}</p>
                            </v-col>
                          </v-row>
                          <app-btn small depressed color="primary" class="mt-2"
                            @click="appendMedicalRecordToAnnotations">
                            <v-icon left small>mdi-plus</v-icon>
                            Inserir nas anotações
                          </app-btn>
                        </div>
                      </v-tab-item>

                    </v-tabs-items>
                    <div class="mt-3 pt-3" style="border-top: 1px solid rgba(0,0,0,.08);">
                      <div class="d-flex flex-wrap" style="gap: 8px;">
                        <app-btn small outlined color="grey darken-2" @click="newRecording">
                          <v-icon left small>mdi-microphone-plus</v-icon>
                          Gravar novo áudio
                        </app-btn>
                        <app-btn small outlined :color="clearTranscriptionConfirming ? 'error' : 'grey darken-2'"
                          @click="handleClearTranscription">
                          <v-icon left small>{{ clearTranscriptionConfirming ? 'mdi-alert' : 'mdi-delete-outline' }}</v-icon>
                          {{ clearTranscriptionConfirming ? 'Isso apaga a transcrição salva, Confirmar?' : 'Limpar transcrição' }}
                        </app-btn>
                      </div>
                    </div>
                  </div>
                </template>

              </template>

              <!-- Mensagem para profissional sem permissão -->
              <div v-else class="d-flex align-center">
                <app-icon class="info--text mr-2">info</app-icon>
                <span class="info--text caption">Somente o profissional da sessão pode criar anotações da sessão.</span>
              </div>

            </div>
          </v-tab-item>

          <!-- ══════════════════════════════
               ABA 2 — ARQUIVOS
               ══════════════════════════════ -->
          <v-tab-item>
            <div class="pa-4 pa-md-6">
              <template v-if="hasCustomer">

                <!-- Estado vazio -->
                <div v-if="!hasFiles" class="text-center py-8">
                  <v-icon size="48" color="grey lighten-1">mdi-folder-outline</v-icon>
                  <div class="body-2 grey--text mt-3 mb-4">Nenhum arquivo anexado ainda</div>
                  <app-btn small outlined color="grey darken-2" @click="handleFileForm">
                    <v-icon left small>mdi-plus</v-icon>
                    Anexar arquivo
                  </app-btn>
                </div>

                <!-- Lista de arquivos -->
                <template v-else>
                  <div class="d-flex justify-end mb-3">
                    <app-btn small outlined color="grey darken-2" @click="handleFileForm">
                      <v-icon left small>mdi-plus</v-icon>
                      Anexar arquivo
                    </app-btn>
                  </div>
                  <app-pagination :showCount="false" @click="selectFiles($event)" :data="files">
                    <v-list dense>
                      <template v-for="(file, index) in files.data">
                        <v-list-item :key="index">
                          <v-list-item-icon class="mr-3 my-auto">
                            <v-icon color="grey darken-1" small>mdi-file-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="body-2">{{ file.name }}</v-list-item-title>
                            <v-list-item-subtitle class="caption">
                              {{ $format.bytesToMB(file.size) }} · {{ $format.dateBr(file.created_at) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                          <div class="d-flex align-center flex-shrink-0 ml-2">
                            <v-btn @click.stop="handleDownloadFile(file)" icon small color="primary">
                              <v-icon small>mdi-download</v-icon>
                            </v-btn>
                            <app-delete-btn @click="handleDeleteFile(file.id)" type="icon" />
                          </div>
                        </v-list-item>
                        <v-divider :key="'d' + file.id" v-if="index" />
                      </template>
                    </v-list>
                  </app-pagination>
                </template>

              </template>
            </div>
          </v-tab-item>

        </v-tabs-items>
      </v-card-text>

      <!-- RODAPÉ FIXO -->
      <v-divider />
      <v-card-actions class="mt-0 pa-4">
        <app-back-btn @click="handleClose()" />
        <v-spacer v-if="$vuetify.breakpoint.xsOnly" />
        <app-confirm-btn @click="update()" />
      </v-card-actions>

    </v-card>

    <!-- Componentes auxiliares (sem alteração) -->
    <template v-if="hasCustomer">
      <FileUploadForm @store="selectFiles()" ref="FileUploadForm" title="Anexar Arquivos à Sessão" :params="fileParams"
        :url="url" />
    </template>
    <AppConfirmDiscardChanges ref="appConfirmDiscardChanges" />
    <TextGenerationForm ref="TextGenerationForm" @store="setText" />

    <input class="d-none" ref="transcriptionInput" id="transcriptionInput" type="file"
      accept=".3gp,.3gpp,.aac,.flac,.m4a,.mp3,.ogg,.opus,.wav,.webm,.wma" @input="handleFileInput" />
  </v-dialog>
</template>

<script>
import FileUploadForm from "@/components/app/forms/FileUploadForm";
import AppConfirmDiscardChanges from "@/components/app/sections/AppConfirmDiscardChanges";
import TextGenerationForm from "@/components/assistant/forms/TextGenerationForm.vue";
import RecordingStorage from "@/api/RecordingStorage";

export default {
  components: { FileUploadForm, AppConfirmDiscardChanges, TextGenerationForm },
  data() {
    return {
      url: "/customer/customer-files",
      session: {
        professional: {},
        status: "shown",
        customer: {},
        session_group: {},
        annotations: null,
      },

      files: {},
      dialog: false,

      // Auto-save
      autoSaveTimer: null,
      autoSaveStatus: null,
      hasAutoSaved: false,

      // Transcrição
      pollingInterval: null,
      recoveredPendingSend: false,
      transcription: {
        status: null,
        job_id: null,
        transcript: null,
        medical_record: null,
        sentiment_analysis: null,
        summary: null,
        loading: false,
        error: null,
      },

      // Tab state
      activeTab: 0,
      transcriptionTab: 0,

      // Confirmação de "Regravar" (ação destrutiva: descarta a gravação concluída)
      restartConfirming: false,
      restartConfirmTimer: null,

      // Confirmação de "Limpar transcrição" (ação destrutiva: apaga a transcrição salva)
      clearTranscriptionConfirming: false,
      clearTranscriptionConfirmTimer: null,
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    customerName() {
      return this.session.origin == "booking"
        ? this.session.booking.name
        : this.session.customer.name;
    },

    hasCustomer() {
      return this.session.customer_id != null;
    },

    canEditSessionAnnotations() {
      return this.user?.id === this.session.professional?.id;
    },

    fileParams() {
      return {
        session_id: this.session.id,
        customer_id: this.session.customer?.id,
        professional_id: this.session.professional?.id,
        collection: "session",
      };
    },

    hasFiles() {
      return this.files.data && this.files.data.length > 0;
    },

    hasText() {
      return this.session.annotations && this.session.annotations.length > 0;
    },

    canUserIa() {
      return this.canEditSessionAnnotations && this.$acl.isProfessional();
    },

    transcriptionLimitReached() {
      return this.user?.transcription_used_credits >= this.user?.transcription_credits;
    },

    hasUnsavedChanges() {
      return this.$store.state.app.hasUnsavedChanges;
    },

    transcriptionStatus() {
      const labels = {
        uploading: "Enviando arquivo...",
        pending: "Procesando...",
        transcribing: "Transcrevendo...",
        analyzing: "Analisando com I.A...",
        done: "Transcrição concluída",
        error: "Erro no processamento",
      };
      const colors = {
        uploading: "grey",
        pending: "grey",
        transcribing: "orange",
        analyzing: "blue",
        done: "primary",
        error: "error",
      };
      const icons = {
        uploading: "mdi-upload",
        pending: "mdi-clock-outline",
        transcribing: "mdi-microphone",
        analyzing: "mdi-brain",
        done: "mdi-check-circle",
        error: "mdi-alert-circle",
      };
      const status = this.transcription.status;
      return {
        icon: icons[status] || "mdi-clock-outline",
        color: colors[status] || "grey",
        label: labels[status] || status,
      };
    },

    formattedDuration() {
      const m = Math.floor(this.duration / 60).toString().padStart(2, '0');
      const s = (this.duration % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    },

    supportsMeetingMode() {
      return !!(navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function');
    },

    // ---- Gravador (estado vive no módulo Vuex recorder, sobrevive à destruição deste componente) ----
    recorderState() {
      return this.$store.state.recorder.recorderState;
    },

    recordingMode() {
      return this.$store.state.recorder.recordingMode;
    },

    duration() {
      return this.$store.state.recorder.duration;
    },

    recorderErrorMessage() {
      return this.$store.state.recorder.recorderErrorMessage;
    },

    isPaused() {
      return this.$store.state.recorder.isPaused;
    },

    recordingSession() {
      return this.$store.state.recorder.recordingSession;
    },

    isRecordingOtherSession() {
      return !!this.recordingSession && this.recordingSession.id !== this.session.id;
    },

    // Só existe blob gravado quando o áudio veio do gravador embutido; um envio via
    // "Enviar arquivo" (upload direto) nunca passa pelo engine do recorder.js.
    hasRecordedAudio() {
      return this.$store.state.recorder.hasRecordedBlob;
    },

    recordingModeText() {

      let text
      if (this.recorderState === 'requesting') {

        text = this.recordingMode === 'meeting' ? 'Aguardando permissões de microfone e tela...' : 'Aguardando permissão do microfone...';
      } else if (this.recorderState === 'recording') {
        if (this.isPaused) {
          text = 'Pausado';
        } else {
          text = this.recordingMode === 'meeting' ? 'Gravando reunião...' : 'Gravando...';
        }
      } else {
        text = '';
      }



      return text;











    },
  },

  watch: {
    session: {
      handler() {
        this.$store.dispatch("app/hasChanges");
        clearTimeout(this.autoSaveTimer);
        this.autoSaveTimer = setTimeout(() => {
          if (this.dialog && this.canEditSessionAnnotations) {
            this.autoSave();
          }
        }, 2000);
      },
      deep: true,
    },

    // Uma gravação nova concluída (recording -> stopped) precisa limpar qualquer
    // resultado/erro de transcrição de uma tentativa anterior, senão a tela
    // continua mostrando o resultado/erro antigo em vez do card "Gravação concluída".
    // Cancelar antes de chegar em 'stopped' não passa por aqui, então uma tentativa
    // de nova gravação cancelada preserva a transcrição antiga (comportamento
    // intencional, ver comentário em newRecording()).
    recorderState(newVal, oldVal) {
      if (newVal === 'stopped' && oldVal === 'recording') {
        this.transcription = {
          status: null,
          job_id: null,
          transcript: null,
          medical_record: null,
          sentiment_analysis: null,
          summary: null,
          loading: false,
          error: null,
        };
      }
    },
  },

  methods: {
    async open(session) {

      this.stopPolling();
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
      this.autoSaveStatus = null;
      this.hasAutoSaved = false;
      this.recoveredPendingSend = false;
      this.transcription = {
        status: null,
        job_id: null,
        transcript: null,
        medical_record: null,
        sentiment_analysis: null,
        summary: null,
        loading: false,
        error: null,
      };

      const isSameRecordingSession = this.recordingSession && this.recordingSession.id === session.id;
      if (!isSameRecordingSession) {
        this.$store.dispatch('recorder/cancelRecording');
      }

      this.activeTab = 0;
      this.transcriptionTab = 0;

      this.session = JSON.parse(JSON.stringify(session));

      // Se há uma gravação em andamento (não enviada) para esta sessão, prioriza mostrar
      // o estado da gravação em vez de uma transcrição antiga já salva no backend.
      if (session.transcription_transcript && !isSameRecordingSession) {
        this.transcription = {
          status: 'done',
          job_id: null,
          transcript: session.transcription_transcript,
          summary: session.transcription_summary,
          sentiment_analysis: session.transcription_sentiment_analysis,
          medical_record: session.transcription_medical_record,
          loading: false,
          error: null,
        };
      }

      // Gravação persistida (sobrou de um crash, reload, ou falha de envio anterior): recupera
      // e tenta reenviar sozinho uma vez, sobrepondo qualquer transcrição antiga acima.
      if (!isSameRecordingSession) {
        const pendingMeta = await RecordingStorage.getMeta(session.id).catch(() => null);
        if (pendingMeta) {
          await this.$store.dispatch('recorder/restoreFromPersisted', { sessionId: session.id, meta: pendingMeta });
          // Se a recuperação falhou (ex.: chunks perdidos no IndexedDB), o recorder fica em
          // estado 'error' com hasRecordedBlob false — não há nada para reenviar.
          if (this.$store.state.recorder.hasRecordedBlob) {
            this.transcription = {
              status: null, job_id: null, transcript: null, medical_record: null,
              sentiment_analysis: null, summary: null, loading: false, error: null,
            };
            this.recoveredPendingSend = true;
            this.sendRecording();
          }
        }
      }

      await this.selectFiles();

      this.$store.dispatch("app/resetChanges");

      this.dialog = true;
    },

    handleClose() {
      if (this.hasUnsavedChanges) {
        this.$refs.appConfirmDiscardChanges
          .askForSave()
          .then((res) => {
            this.update();
          })
          .catch((error) => {
            this.close();
          });
      } else {
        this.close();
      }
    },

    close() {
      this.stopPolling();
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
      this.autoSaveStatus = null;
      this.$store.dispatch("app/resetChanges");
      this.dialog = false;
      if (this.hasAutoSaved) {
        this.$emit("store", this.session.id);
      }
    },

    save() {
      return this.$http.update("sessions/session-record", this.session.id, this.session);
    },

    update() {
      this.save()
        .then(() => {
          this.processSaved();
        })
        .catch(() => {
          this.$store.dispatch("app/notification", {
            type: "error",
            message: "Erro ao salvar. Tente novamente.",
          });
        });
    },

    autoSave() {
      this.autoSaveStatus = 'saving';
      this.save()
        .then(() => {
          this.$store.dispatch("app/resetChanges");
          this.hasAutoSaved = true;
          this.autoSaveStatus = 'saved';
          setTimeout(() => {
            if (this.autoSaveStatus === 'saved') this.autoSaveStatus = null;
          }, 3000);
        })
        .catch(() => {
          this.autoSaveStatus = null;
        });
    },

    processSaved() {
      this.$emit("store", this.session.id);

      this.$store.dispatch("app/resetChanges");

      this.dialog = false;
    },

    handleFileForm() {
      this.$refs.FileUploadForm.open();
    },

    async selectFiles(page) {
      if (this.hasCustomer) {
        this.fileParams.page = page;
        this.$loading.start();
        await this.$http
          .index(this.url, this.fileParams)
          .then((response) => {
            this.files = response.files;
            this.$loading.finish();
          })
          .catch((error) => {
            this.$loading.finish();
          });
      }
    },

    handleDownloadFile(file) {
      this.$loading.start();
      this.$http
        .download(`${this.url}/download`, { id: file.id })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", file.name);
          document.body.appendChild(link);
          link.click();
          this.$loading.finish();
        })
        .catch((error) => {
          this.$loading.finish();
        });
    },

    async downloadRecording() {
      const blob = await this.$store.dispatch('recorder/getRecorderBlob');
      if (!blob) return;
      const ext = blob.type.includes('ogg') ? 'ogg' : blob.type.includes('mp4') ? 'mp4' : 'webm';
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gravacao-sessao-${this.session.id}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    handleDeleteFile(document_id) {
      this.$loading.start();
      this.$http
        .destroy(this.url, document_id)
        .then((response) => {
          this.selectFiles();
          this.$loading.finish();
        })
        .catch((error) => {
          this.selectFiles();
          this.$loading.finish();
        });
    },

    handleTextGenerationForm() {
      this.$refs.TextGenerationForm.open(this.session.annotations);
    },

    setText(text) {
      this.session.annotations = text;
    },


    // ---- Transcrição ----
    openTranscriptionPanel() {
      if (this.recorderState === 'mode') {
        this.cancelRecording();
      }
      this.$refs.transcriptionInput.value = null;
      this.$nextTick(() => {
        this.$refs.transcriptionInput.click();
      });
    },

    newRecording() {
      // Não zera this.transcription aqui: se o usuário desistir e cancelar a
      // gravação, a transcrição antiga precisa continuar disponível na tela
      // (ver condição do recorder-box no template, que usa recorderState).
      this.stopPolling();
      this.$store.dispatch('recorder/newRecording');
    },


    handleFileInput(event) {
      const file = event.target.files[0];
      if (file) this.sendTranscription(file);
    },

    async sendTranscription(file) {
      if (!file) return;

      this.transcription.loading = true;
      this.transcription.status = "uploading";
      this.transcription.error = null;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("session_id", this.session.id);

      try {
        const response = await this.$http.store("sessions/transcription", formData);

        if (!response || !response.job_id) {
          throw new Error("Resposta inválida da API");
        }

        this.transcription.job_id = response.job_id;
        this.transcription.status = response.status || "pending";
        this.transcription.loading = false;

        this.startPolling();
      } catch (error) {
        this.transcription.status = "error";
        this.transcription.error = "Falha ao enviar o arquivo para transcrição.";
        this.transcription.loading = false;
      }
    },

    startPolling() {
      this.pollingInterval = setInterval(async () => {
        await this.checkStatus();
      }, 7000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      if (this.transcription.timer) {
        clearInterval(this.transcription.timer);
        this.transcription.timer = null;
      }
    },

    async checkStatus() {
      await this.$http.index(`sessions/transcription/status/${this.transcription.job_id}`)
        .then(async (response) => {

          this.transcription.status = response.status;

          if (response.status === "done") {
            this.stopPolling();
            await this.fetchResult();
          }

          if (response.status === "error") {
            this.stopPolling();
            this.transcription.error = response.error_message || "Erro no processamento da transcrição";
          }
        })
        .catch((error) => {
          this.transcription.status = "error";
          this.stopPolling();
        });
    },

    async fetchResult() {
      try {
        const response = await this.$http.index(`sessions/transcription/result/${this.transcription.job_id}`, { session_id: this.session.id });
        this.transcription.summary = response.summary;
        this.transcription.sentiment_analysis = response.sentiment_analysis;
        this.transcription.medical_record = response.medical_record;
        this.transcription.transcript = response.transcript;

        if (response.transcript) {
          this.$store.commit("onboarding/transcriptionStored");
        }

        this.$store.dispatch("recorder/confirmSent");
      } catch (error) {
        this.transcription.status = "error";
        this.transcription.error = "Falha ao buscar o resultado da transcrição.";
      } finally {
        this.transcription.loading = false;
      }
    },

    appendToAnnotations(text) {
      this.session.annotations = this.session.annotations
        ? `${this.session.annotations}\n\n${text}`
        : text;
    },

    appendTranscriptToAnnotations() {
      this.appendToAnnotations(this.transcription.transcript);
    },

    appendSummaryToAnnotations() {
      const s = this.transcription.summary;
      let text = "=== Resumo da Sessão ===\n";
      if (s.principais_temas?.length) text += `Principais temas: ${s.principais_temas.join(", ")}\n`;
      if (s.progresso_observado) text += `Progresso: ${s.progresso_observado}\n`;
      if (s.pontos_de_atencao?.length) text += `Pontos de atenção: ${s.pontos_de_atencao.join("; ")}\n`;
      if (s.duracao_estimada) text += `Duração estimada: ${s.duracao_estimada}`;
      this.appendToAnnotations(text);
    },

    appendSentimentToAnnotations() {
      const s = this.transcription.sentiment_analysis;
      let text = "=== Análise Emocional ===\n";
      if (s.estado_emocional_geral) text += `Estado emocional: ${s.estado_emocional_geral}\n`;
      if (s.nivel_engajamento) text += `Nível de engajamento: ${s.nivel_engajamento}\n`;
      if (s.emocoes_identificadas?.length) text += `Emoções identificadas: ${s.emocoes_identificadas.join(", ")}\n`;
      if (s.momentos_criticos?.length) text += `Momentos críticos: ${s.momentos_criticos.join("; ")}\n`;
      if (s.observacoes_clinicas) text += `Observações clínicas: ${s.observacoes_clinicas}`;
      this.appendToAnnotations(text);
    },

    // ---- Gravador (lógica no módulo Vuex recorder, ver store/modules/sessions/recorder.js) ----
    openModeChoice() {
      this.$store.dispatch('recorder/openModeChoice');
    },

    toggleModeChoice() {
      if (this.recorderState === 'mode') {
        this.cancelRecording();
      } else {
        this.openModeChoice();
      }
    },

    chooseMode(mode) {
      this.$store.dispatch('recorder/chooseMode', {
        mode,
        session: { id: this.session.id, customerName: this.customerName },
      });
    },

    startOnlineCapture() {
      this.$store.dispatch('recorder/startOnlineCapture');
    },

    stopRecording() {
      this.$store.dispatch('recorder/stopRecording');
    },

    togglePause() {
      if (this.isPaused) {
        this.$store.dispatch('recorder/resumeRecording');
      } else {
        this.$store.dispatch('recorder/pauseRecording');
      }
    },

    restartRecording() {
      this.$store.dispatch('recorder/restartRecording');
    },

    handleRestartRecording() {
      if (this.restartConfirming) {
        clearTimeout(this.restartConfirmTimer);
        this.restartConfirming = false;
        this.restartRecording();
        return;
      }
      this.restartConfirming = true;
      this.restartConfirmTimer = setTimeout(() => {
        this.restartConfirming = false;
      }, 10000);
    },

    retryAfterError() {
      this.$store.dispatch('recorder/retryAfterError');
    },

    handleClearTranscription() {
      if (this.clearTranscriptionConfirming) {
        clearTimeout(this.clearTranscriptionConfirmTimer);
        this.clearTranscriptionConfirming = false;
        this.clearTranscription();
        return;
      }
      this.clearTranscriptionConfirming = true;
      this.clearTranscriptionConfirmTimer = setTimeout(() => {
        this.clearTranscriptionConfirming = false;
      }, 10000);
    },

    clearTranscription() {
      this.$loading.start();
      this.$http.destroy("sessions/transcription", this.session.id)
        .then(() => {
          this.transcription = {
            status: null,
            job_id: null,
            transcript: null,
            medical_record: null,
            sentiment_analysis: null,
            summary: null,
            loading: false,
            error: null,
          };
          this.$loading.finish();
        })
        .catch(() => {
          this.$loading.finish();
          this.$store.dispatch("app/notification", {
            type: "error",
            message: "Erro ao limpar a transcrição. Tente novamente.",
          });
        });
    },

    async sendRecording() {
      const file = await this.$store.dispatch('recorder/sendRecording');
      if (!file) return;
      this.sendTranscription(file);
    },

    // "Tentar novamente" no bloco de erro: se há áudio gravado (veio do gravador
    // embutido), reenvia de verdade. Se não (o erro veio de um upload via "Enviar
    // arquivo", que nunca passa pelo engine do recorder.js), não há nada para
    // reenviar — volta ao estado inicial para o usuário escolher Gravar/Enviar de novo.
    retryTranscriptionAttempt() {
      if (this.hasRecordedAudio) {
        this.sendRecording();
      } else {
        this.transcription = {
          status: null,
          job_id: null,
          transcript: null,
          medical_record: null,
          sentiment_analysis: null,
          summary: null,
          loading: false,
          error: null,
        };
      }
    },

    cancelRecording() {
      this.$store.dispatch('recorder/cancelRecording');
    },

    appendMedicalRecordToAnnotations() {
      const m = this.transcription.medical_record;
      let text = "=== Prontuário Sugerido ===\n";
      if (m.queixa_principal) text += `Queixa principal: ${m.queixa_principal}\n`;
      if (m.conteudo_sessao) text += `Conteúdo da sessão: ${m.conteudo_sessao}\n`;
      if (m.intervencoes_realizadas?.length) text += `Intervenções: ${m.intervencoes_realizadas.join(", ")}\n`;
      if (m.reacoes_do_paciente) text += `Reações do paciente: ${m.reacoes_do_paciente}\n`;
      if (m.plano_proxima_sessao) text += `Plano próxima sessão: ${m.plano_proxima_sessao}\n`;
      if (m.observacoes_adicionais) text += `Observações adicionais: ${m.observacoes_adicionais}`;
      this.appendToAnnotations(text);
    },
  },

  beforeDestroy() {
    clearTimeout(this.restartConfirmTimer);
    clearTimeout(this.clearTranscriptionConfirmTimer);
  },
};
</script>

<style scoped>
.autosave-fade-enter-active,
.autosave-fade-leave-active {
  transition: opacity 0.4s;
}

.autosave-fade-enter,
.autosave-fade-leave-to {
  opacity: 0;
}

.recorder-fade-enter-active,
.recorder-fade-leave-active {
  transition: opacity 0.15s;
}

.recorder-fade-enter,
.recorder-fade-leave-to {
  opacity: 0;
}

.recording-player {
  width: 100%;
  height: 36px;
  outline: none;
}

.recorder-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0 8px;
  text-align: center;
}

.recorder-pulse {
  display: inline-flex;
  border-radius: 50%;
  padding: 12px;
  animation: pulse 1.5s ease-in-out infinite;
}

.recorder-pulse--paused {
  animation: none;
}

.online-guide-row {
  width: 100%;
  max-width: 340px;
  flex-wrap: wrap;
}

.online-guide-label {
  min-width: 96px;
}

.ui-label {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(0, 0, 0, .06);
  color: rgba(0, 0, 0, .78);
  font-weight: 500;
  font-size: 11px;
  line-height: 1.6;
  white-space: nowrap;
}

@keyframes pulse {

  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4);
  }

  50% {
    box-shadow: 0 0 0 16px rgba(244, 67, 54, 0);
  }
}
</style>
